import React, { useEffect, useState } from 'react';
import { isNil, isEqual } from 'lodash';
// Components
import FixedValueComponent from './FixedValueComponent';
import RequiredMark from 'shared/inputs/RequiredMark';
import Select from 'shared/inputs/BaseSelect';
import Toggle from 'shared/inputs/BaseToggle';
import MappingRule from './MappingRule';
// Consts
import { FIXED_VALUE_INDICATOR } from '../constants';

const defaultValues = [];
const defaultProps = [];
const defaultSheetColumns = [];

const MappingOrderProperties = ({
  values = defaultValues,
  setData,
  orderProperties = defaultProps,
  sheetColumns = defaultSheetColumns,
  search,
  filledOnly,
  setHasNotSubmittedChanges,
  setMappingsSet,
}) => {
  const [innerValues, setInnerValues] = useState([...values]);

  useEffect(() => {
    if (isEqual(values, innerValues)) {
      return;
    }
    setInnerValues([...values]);
  }, [values]);

  useEffect(() => {
    if (isEqual(values, innerValues)) {
      return;
    }
    setData([...innerValues]);
    if (setMappingsSet) {
      setMappingsSet(innerValues.filter((o) => !isNil(o.mappingPropertyId)));
    }
    if (setHasNotSubmittedChanges) {
      setHasNotSubmittedChanges(true);
    }
  }, [innerValues]);

  const setFieldValueForMappings = (field, value, id) => {
    setInnerValues((prev) => {
      const currData = [...prev];

      const targetIndex = currData.findIndex((x) => x.mappingPropertyId === id);

      // Add new mapping item
      if (targetIndex < 0) {
        const newItem = { [field]: value, mappingPropertyId: id };
        if (field === 'columnName' && value !== FIXED_VALUE_INDICATOR) {
          newItem.fixedValue = null;
        }
        return [...currData, newItem];
      }

      // Update existing item mapping
      const result = currData.map((item, ind) => {
        if (ind === targetIndex) {
          const newItem = { ...item, [field]: value };
          if (field === 'columnName' && value !== FIXED_VALUE_INDICATOR) {
            newItem.fixedValue = null;
          }
          return newItem;
        }
        return item;
      });

      return result;
    });
  };

  const getOrderMappingRulesFieldValue = (name, id) => {
    const value = innerValues?.find((x) => x.mappingPropertyId === id);

    if (value && !isNil(value[name])) {
      return value[name];
    }

    return null;
  };

  const colOpts = sheetColumns.length > 0
    ? sheetColumns.map((x) => ({ label: x, value: x }))
    : innerValues
      ?.filter((c) => c.columnName !== null && c.columnName !== FIXED_VALUE_INDICATOR && c.columnName != null)
      ?.map((x) => x.columnName)
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((x) => ({ label: x, value: x }));

  const columnOptions = [
    { label: '', value: null },
    { label: 'Fixed Value', value: FIXED_VALUE_INDICATOR },
    ...colOpts,
  ];

  const getColumnMapperDefaultValue = (id) => {
    const fixedValue = getOrderMappingRulesFieldValue('fixedValue', id);

    if (!isNil(fixedValue)) {
      return { label: 'Fixed Value', value: FIXED_VALUE_INDICATOR };
    }
    return null;
  };

  // Sort fields by required flag and name (show required first)
  const sorted = orderProperties.sort((a, b) => {
    const { required: aRequired, displayName: aName } = a;
    const { required: bRequired, displayName: bName } = b;

    // eslint-disable-next-line no-nested-ternary
    const ind = aName > bName ? 1 : aName < bName ? -1 : 0;

    if (aRequired && bRequired) {
      return ind;
    }
    if (aRequired) {
      return -1;
    }
    if (bRequired) {
      return 1;
    }
    return ind;
  }) ?? [];

  // Filter
  const visibleFields = sorted.filter(({ id, displayName }) => {
    const hasSearchSubstring = displayName.toLowerCase().includes(search.toLowerCase());
    const isSelected = !isNil(getOrderMappingRulesFieldValue('columnName', id)
      ?? getOrderMappingRulesFieldValue('fixedValue', id));
    return filledOnly ? hasSearchSubstring && isSelected : hasSearchSubstring;
  });

  const fieldsList = visibleFields.map((val) => {
    const { id, displayName, required } = val;

    return (
      <div key={`columnName-${id}`} className='fields-list-item'>
        <div className='property-field'>
          {displayName}
          {required && <RequiredMark />}
        </div>
        <div className='property-field'>
          <Select
            data={columnOptions}
            textField='label'
            defaultValue={getColumnMapperDefaultValue(id)}
            value={
              getColumnMapperDefaultValue(id)
                ? getColumnMapperDefaultValue(id).value
                : getOrderMappingRulesFieldValue('columnName', id)
            }
            label=''
            isCombo
            onChange={({ value }) => {
              if (isNil(value)) {
                setFieldValueForMappings('columnName', null, id);
              } else {
                setFieldValueForMappings('columnName', value.value, id);
              }
            }}
          />
        </div>
        <div className='property-field'>
          {(getOrderMappingRulesFieldValue('columnName', id) === FIXED_VALUE_INDICATOR
            || !isNil(getOrderMappingRulesFieldValue('fixedValue', id))) && (
            <FixedValueComponent
              fixedValueProps={val}
              name='fixedValue'
              value={getOrderMappingRulesFieldValue('fixedValue', id)}
              displayValue={getOrderMappingRulesFieldValue('fixedValueName', id)}
              setFieldValue={(val) => setFieldValueForMappings('fixedValue', val, id)}
              setDisplayValue={(val) => setFieldValueForMappings('fixedValueName', val, id)}
            />
          )}
          {(!(getOrderMappingRulesFieldValue('columnName', id) === FIXED_VALUE_INDICATOR
            || !isNil(getOrderMappingRulesFieldValue('fixedValue', id)))
            && !!getOrderMappingRulesFieldValue('columnName', id)) && (
            <MappingRule
              onSubmit={(value) => setFieldValueForMappings('transformation', value, id)}
              value={getOrderMappingRulesFieldValue('transformation', id)}
            />
          )}
        </div>
        <div className='property-field small-field'>
          <Toggle
            checked={getOrderMappingRulesFieldValue('overwriteOnMerge', id)}
            onChange={({ value }) => {
              setFieldValueForMappings('overwriteOnMerge', value, id);
            }}
          />
        </div>
      </div>
    );
  });

  return fieldsList;
};

export default MappingOrderProperties;
